import { motion } from "framer-motion";
import { Pill, Plus, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import FeatureHeader from "../components/FeatureHeader";
import Loader from "../components/loader";

export default function AllCategories() {
  const navigate = useNavigate();
  const [trending, setTrending] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const loaderRef = useRef(null);

  const handleAddToCart = async (productId) => {
    try {
      const response = await api.post("/api/cart/add", {
        medicineId: productId,
        dose: "0mg",
        quantity: 1,
      });
      showAddToCartSuccess();
      console.log("Cart response:", response.data);
    } catch (err) {
      console.error(err.response?.data || err.message);
      alert("Failed to add to cart. Make sure you are logged in!");
    }
  };

  const showAddToCartSuccess = () => {
    const successEl = document.createElement("div");
    successEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #10B981, #059669);
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      z-index: 9999;
      box-shadow: 0 8px 32px rgba(16, 185, 129, 0.3);
      animation: slideIn 0.3s ease;
      font-weight: 600;
    `;
    successEl.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        Added to cart successfully!
      </div>
    `;
    document.body.appendChild(successEl);
    setTimeout(() => {
      successEl.style.animation = "slideOut 0.3s ease forwards";
      setTimeout(() => document.body.removeChild(successEl), 300);
    }, 2000);
  };

  useEffect(() => {
    const fetchMedicines = async () => {
      if (!hasMore) return;
      setLoading(true);
      try {
        const res = await api.get(`api/medicines/?page=${page}&limit=10`);
        setTrending((prev) => [...prev, ...res.data.medicines]);
        setHasMore(page < res.data.totalPages);
      } catch (err) {
        console.error("Error fetching medicines:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMedicines();
  }, [page]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore && !loading) {
        setPage((prev) => prev + 1);
      }
    });
    const current = loaderRef.current;
    if (current) observer.observe(current);
    return () => current && observer.unobserve(current);
  }, [hasMore, loading]);

  return (
    <section style={styles.page}>

      {/* Header */}




      <FeatureHeader

      title = "All Available Drugs"
      subtitle = "All medicines available in the pharmacy"
      onBack = {() => navigate(-1)}
      />

      {/* Drug Grid */}
      <div style={styles.grid}>
        {trending.map((drug) => (
          <ProductCard key={drug._id} product={drug} navigate={navigate} handleAddToCart={handleAddToCart} />
        ))}
      </div>

      {/* Loader */}
      <div ref={loaderRef} style={{ textAlign: "center", padding: 20 }}>
        {loading && <Loader />}
        {!hasMore && <span>No more trending drugs</span>}
      </div>
    </section>
  );
}


const ProductCard = ({ product, navigate, handleAddToCart }) => {
  return (
    <motion.div
      style={styles.productCard}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.95 }}
    >
      <div onClick={() => navigate(`/product/${product._id}`)}>
        <div style={styles.productImageContainer}>
          <img
            src={product.images?.[0] || ''}
            alt={product.name}
            style={styles.productImage}
            loading="lazy"
          />
          <div style={{
            ...styles.prescriptionBadge,
            backgroundColor: product.prescriptionRequired ? '#7914f450' : ''
          }}>
            {product.prescriptionRequired ? "Prescription Required" : ""}
          </div>
          <motion.button
            style={styles.cartButton}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            onClick={(e) => {
              e.stopPropagation();
              handleAddToCart(product._id);
            }}
            aria-label={`Add ${product.name} to cart`}
          >
            <Plus size={16} />
          </motion.button>
        </div>
        <div style={styles.productInfo}>
          <h6 style={styles.productName}>{product.name}</h6>
          <div style={styles.priceContainer}>
            {product.originalPrice > product.price && (
              <span style={styles.originalPrice}>${product.originalPrice}</span>
            )}
            <span style={styles.currentPrice}>${product.price}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #ffffff 0%, #faf7ff 100%)",
    fontFamily: "'Poppins', sans-serif",
    padding: "0px 16px 0px",
    position: "relative",
  },
  backButton: {
    position: "fixed",
    top: 20,
    left: 16,
    background: "linear-gradient(135deg, #7e57c2, #673ab7)",
    borderRadius: "50%",
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    cursor: "pointer",
    zIndex: 100,
  },
  header: {
    marginBottom: 20,
    textAlign: "left",
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#2e1065",
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: "#6b7280",
    marginTop: 6,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 16,
  },
  prescriptionBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    padding: '3px 8px',
    borderRadius: 10,
    color: '#fff',
    fontSize: 9,
    fontWeight: 700,
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  productCard: {
    flex: "0 0 164px",
    minWidth: "164px",
    maxWidth: "164px",
    scrollSnapAlign: "start",
    backgroundColor: "transparent",
    padding: 0,
    boxShadow: "none",
    border: "none",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  productImageContainer: {
    position: 'relative',
    height: '200px',
    overflow: 'hidden',
    borderRadius: '6px',
    background: '#ededf0',
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  cartButton: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    background: 'white',
    border: 'solid 1.5px var(--primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.22)',
    color: 'var(--primary)',
  },
  productInfo: {
    marginTop: '5px',
    marginBottom: '2px',
    padding: '0 0 10',
    lineHeight: '1.3',
  },
  productName: {
    display: 'block',
    width: '100%',
    fontSize: '13px',
    fontWeight: '200',
    color: '#18141f',
    margin: '0',
    lineHeight: '1.3',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'left',
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  currentPrice: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#1f1a28',
    marginLeft: 0,
  },
  originalPrice: {
    fontSize: '11px',
    marginLeft: 0,
    color: '#9b93ab',
    textDecoration: 'line-through',
  },
  skeletonCard: {
    height: 190,
    borderRadius: 16,
    background:
      "linear-gradient(90deg, #f2f2f2 25%, #e0e0e0 50%, #f2f2f2 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.5s infinite",
  },
};
